import { useState, useEffect, useCallback, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════
   YOURS TRULY — THE ATELIER
   Admin dashboard. Submissions arrive as iceberg cards you can
   click to expand. Reads from /api/db?type=submissions, parses the
   brief_text JSON blob written by AppV3's submitIceberg.

   brief_text shape:
     { iceberg: { name, relationship,
                  tier1:[{id,text,category}], tier2:[...], tier3:[...],
                  emotional_core, gift_subtext },
       selected: [id, ...],
       conversation: [{role, content}, ...] }
   ═══════════════════════════════════════════════════════════════ */

const API = "/api/submissions";

/* ─── Themes (match the landing page) ─────────────────────────── */
const DARK = {
  name: "dark",
  bg: "#0a0a14",
  bgSoft: "#0e0e1c",
  bgCard: "rgba(255,255,255,0.025)",
  bgCardHover: "rgba(255,255,255,0.045)",
  bgInset: "rgba(255,255,255,0.02)",
  text: "#ede9e3",
  textSoft: "#9a9490",
  textMuted: "#5e5954",
  textFaint: "#3a3640",
  accent: "#c9a96e",
  accentSoft: "rgba(201,169,110,0.5)",
  border: "rgba(201,169,110,0.10)",
  borderLight: "rgba(255,255,255,0.06)",
  shadow: "0 8px 40px rgba(0,0,0,0.5)",
  shadowHover: "0 14px 60px rgba(0,0,0,0.6)",
  glow: "rgba(201,169,110,0.12)",
  t1: { dot: "#b9b4c4", chip: "rgba(185,180,196,0.08)", chipBorder: "rgba(185,180,196,0.16)", text: "#cdc8d6" },
  t2: { dot: "#6f8fd4", chip: "rgba(111,143,212,0.09)", chipBorder: "rgba(111,143,212,0.20)", text: "#9db4e6" },
  t3: { dot: "#d4736f", chip: "rgba(212,115,111,0.09)", chipBorder: "rgba(212,115,111,0.22)", text: "#e6a09d" },
  sel: { ring: "rgba(201,169,110,0.55)", glow: "rgba(201,169,110,0.18)" },
};
const LIGHT = {
  name: "light",
  bg: "#e8e4ee",
  bgSoft: "#f0ecf4",
  bgCard: "rgba(255,255,255,0.6)",
  bgCardHover: "rgba(255,255,255,0.85)",
  bgInset: "rgba(255,255,255,0.45)",
  text: "#2a2830",
  textSoft: "#6a6874",
  textMuted: "#9a98a4",
  textFaint: "#bdbbc6",
  accent: "#9a7d3e",
  accentSoft: "rgba(154,125,62,0.6)",
  border: "rgba(154,125,62,0.18)",
  borderLight: "rgba(160,156,170,0.16)",
  shadow: "0 8px 32px rgba(100,98,110,0.10)",
  shadowHover: "0 16px 48px rgba(100,98,110,0.16)",
  glow: "rgba(154,125,62,0.10)",
  t1: { dot: "#8a8894", chip: "rgba(138,136,148,0.07)", chipBorder: "rgba(138,136,148,0.18)", text: "#56545e" },
  t2: { dot: "#5a7ac0", chip: "rgba(60,90,160,0.06)", chipBorder: "rgba(60,90,160,0.18)", text: "#3c5aa0" },
  t3: { dot: "#b44c4c", chip: "rgba(180,60,60,0.06)", chipBorder: "rgba(180,60,60,0.18)", text: "#9a3636" },
  sel: { ring: "rgba(154,125,62,0.6)", glow: "rgba(154,125,62,0.14)" },
};

const TIER_META = {
  tier1: { key: "t1", label: "The Surface", sub: "what's visible" },
  tier2: { key: "t2", label: "Below the Waterline", sub: "quirks & texture" },
  tier3: { key: "t3", label: "The Deep Core", sub: "what drives them" },
};

/* ─── Helpers ──────────────────────────────────────────────────── */
function parseBrief(row) {
  let parsed = null;
  try {
    parsed = typeof row.brief_text === "string" ? JSON.parse(row.brief_text) : row.brief_text;
  } catch {
    parsed = null;
  }
  // Support both { iceberg, selected, conversation } and a bare iceberg object
  const ice = parsed?.iceberg || parsed || {};
  const selected = new Set(parsed?.selected || []);
  const conversation = parsed?.conversation || [];
  return {
    id: row.id,
    createdAt: row.created_at,
    theme: row.theme || "",
    email: row.session?.user_email || parsed?.email || null,
    source: row.session?.source || null,
    name: ice.name || "Untitled",
    relationship: ice.relationship || null,
    tier1: ice.tier1 || [],
    tier2: ice.tier2 || [],
    tier3: ice.tier3 || [],
    emotional_core: ice.emotional_core || null,
    gift_subtext: ice.gift_subtext || null,
    selected,
    conversation,
    raw: !parsed ? row.brief_text : null, // fallback for un-parseable legacy briefs
  };
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ─── Tier chip ────────────────────────────────────────────────── */
function Chip({ item, tier, selected, T }) {
  const c = T[tier];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 11px",
        borderRadius: 18,
        fontSize: 12,
        lineHeight: 1.3,
        fontFamily: '"Outfit", sans-serif',
        color: selected ? T.accent : c.text,
        background: selected ? T.sel.glow : c.chip,
        border: `1px solid ${selected ? T.sel.ring : c.chipBorder}`,
        boxShadow: selected ? `0 0 0 1px ${T.sel.glow}` : "none",
        transition: "all 0.25s ease",
      }}
    >
      {selected && <span style={{ fontSize: 9, color: T.accent }}>✦</span>}
      {item.text}
    </span>
  );
}

function TierBlock({ tierKey, items, selectedSet, T }) {
  if (!items?.length) return null;
  const meta = TIER_META[tierKey];
  const c = T[meta.key];
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.dot, boxShadow: `0 0 8px ${c.dot}55` }} />
        <span style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: T.textSoft, fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}>
          {meta.label}
        </span>
        <span style={{ fontSize: 11, color: T.textFaint, fontStyle: "italic", fontFamily: '"Cormorant Garamond", serif' }}>
          {meta.sub}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
        {items.map((it) => (
          <Chip key={it.id || it.text} item={it} tier={meta.key} selected={selectedSet.has(it.id)} T={T} />
        ))}
      </div>
    </div>
  );
}

/* ─── Submission card ──────────────────────────────────────────── */
function SubmissionCard({ sub, expanded, onToggle, T, index }) {
  const [hover, setHover] = useState(false);
  const [showConvo, setShowConvo] = useState(false);
  const counts = `${sub.tier1.length}·${sub.tier2.length}·${sub.tier3.length}`;
  const selCount = sub.selected.size;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover || expanded ? T.bgCardHover : T.bgCard,
        border: `1px solid ${expanded ? T.border : T.borderLight}`,
        borderRadius: 18,
        boxShadow: hover || expanded ? T.shadowHover : T.shadow,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        overflow: "hidden",
        transition: "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease",
        transform: hover && !expanded ? "translateY(-2px)" : "none",
        animation: `cardIn 0.5s ease ${index * 0.05}s both`,
      }}
    >
      {/* Clickable header */}
      <div onClick={onToggle} style={{ padding: "20px 24px", cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontWeight: 400, fontSize: 24, color: T.text, lineHeight: 1.1 }}>
              {sub.name}
            </div>
            {sub.relationship && (
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3, fontFamily: '"Outfit", sans-serif', letterSpacing: "0.02em" }}>
                {sub.relationship}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: T.textMuted, fontFamily: '"Outfit", sans-serif' }}>{timeAgo(sub.createdAt)}</div>
            <div style={{ fontSize: 10, color: T.textFaint, marginTop: 4, fontFamily: '"Outfit", sans-serif', letterSpacing: "0.05em" }}>
              {counts} tiers · {selCount} chosen
            </div>
          </div>
        </div>

        {/* Emotional core teaser */}
        {sub.emotional_core && (
          <div
            style={{
              marginTop: 14,
              paddingLeft: 14,
              borderLeft: `2px solid ${T.accentSoft}`,
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 16,
              lineHeight: 1.45,
              color: T.accent,
              opacity: expanded ? 1 : 0.92,
              ...(expanded ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }),
            }}
          >
            {sub.emotional_core}
          </div>
        )}

        {/* Tier preview dots when collapsed */}
        {!expanded && (
          <div style={{ display: "flex", gap: 6, marginTop: 14, alignItems: "center" }}>
            {["t1", "t2", "t3"].map((k, i) => {
              const n = [sub.tier1, sub.tier2, sub.tier3][i].length;
              return n ? (
                <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: T.textMuted, fontFamily: '"Outfit", sans-serif' }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: T[k].dot }} />
                  {n}
                </span>
              ) : null;
            })}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: T.accentSoft, fontFamily: '"Outfit", sans-serif' }}>expand ↓</span>
          </div>
        )}
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: "4px 24px 24px", animation: "fadeIn 0.4s ease" }}>
          <div style={{ height: 1, background: T.borderLight, margin: "0 0 20px" }} />

          {sub.raw ? (
            <div style={{ fontSize: 13, color: T.textSoft, fontFamily: '"Outfit", sans-serif', whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
              {sub.raw}
            </div>
          ) : (
            <>
              <TierBlock tierKey="tier1" items={sub.tier1} selectedSet={sub.selected} T={T} />
              <TierBlock tierKey="tier2" items={sub.tier2} selectedSet={sub.selected} T={T} />
              <TierBlock tierKey="tier3" items={sub.tier3} selectedSet={sub.selected} T={T} />

              {sub.gift_subtext && (
                <div
                  style={{
                    marginTop: 6,
                    padding: "16px 18px",
                    borderRadius: 14,
                    background: T.bgInset,
                    border: `1px solid ${T.border}`,
                  }}
                >
                  <div style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: T.textMuted, marginBottom: 8, fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}>
                    What they're really saying
                  </div>
                  <div style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: 17, lineHeight: 1.5, color: T.text }}>
                    {sub.gift_subtext}
                  </div>
                </div>
              )}

              {/* Meta + conversation */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
                {sub.email && (
                  <span style={{ fontSize: 11, color: T.textMuted, fontFamily: '"Outfit", sans-serif' }}>✉ {sub.email}</span>
                )}
                {sub.theme && (
                  <span style={{ fontSize: 10, color: T.textFaint, fontFamily: '"Outfit", sans-serif', letterSpacing: "0.05em" }}>{sub.theme}</span>
                )}
                <span style={{ flex: 1 }} />
                {sub.conversation.length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowConvo((v) => !v); }}
                    style={{
                      background: "none",
                      border: `1px solid ${T.borderLight}`,
                      borderRadius: 16,
                      padding: "5px 14px",
                      fontSize: 11,
                      color: T.textSoft,
                      cursor: "pointer",
                      fontFamily: '"Outfit", sans-serif',
                      transition: "all 0.2s ease",
                    }}
                  >
                    {showConvo ? "Hide transcript" : `Transcript (${sub.conversation.length})`}
                  </button>
                )}
              </div>

              {showConvo && (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflow: "auto" }}>
                  {sub.conversation.map((m, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                      <div
                        style={{
                          maxWidth: "78%",
                          padding: "8px 13px",
                          borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                          background: m.role === "user" ? T.sel.glow : T.bgInset,
                          border: `1px solid ${m.role === "user" ? T.sel.ring : T.borderLight}`,
                          fontSize: 12,
                          lineHeight: 1.5,
                          color: T.textSoft,
                          fontFamily: '"Outfit", sans-serif',
                        }}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Root ─────────────────────────────────────────────────────── */
export default function Atelier() {
  const [isDark, setIsDark] = useState(true);
  const T = isDark ? DARK : LIGHT;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(API);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.data || []);
    } catch (e) {
      setError(e.message || "Failed to load");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Inject fonts + keyframes once
  useEffect(() => {
    if (!document.getElementById("yt-atelier-fonts")) {
      const link = document.createElement("link");
      link.id = "yt-atelier-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap";
      document.head.appendChild(link);
    }
    if (!document.getElementById("yt-atelier-kf")) {
      const st = document.createElement("style");
      st.id = "yt-atelier-kf";
      st.textContent = `
        @keyframes cardIn { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes spin { to { transform:rotate(360deg); } }
        .yt-atelier ::-webkit-scrollbar { width:8px; height:8px; }
        .yt-atelier ::-webkit-scrollbar-thumb { background:rgba(150,150,160,0.25); border-radius:8px; }
      `;
      document.head.appendChild(st);
    }
  }, []);

  const submissions = useMemo(() => rows.map(parseBrief), [rows]);

  return (
    <div
      className="yt-atelier"
      style={{
        minHeight: "100vh",
        background: T.bg,
        color: T.text,
        fontFamily: '"Outfit", sans-serif',
        transition: "background 0.4s ease, color 0.4s ease",
        backgroundImage:
          isDark
            ? "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(201,169,110,0.06), transparent 60%)"
            : "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255,255,255,0.6), transparent 60%)",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px 80px" }}>
        {/* Header */}
        <div style={{ paddingTop: 48, paddingBottom: 28, display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", color: T.accent, marginBottom: 8, fontWeight: 600 }}>
              Yours Truly
            </div>
            <h1 style={{ margin: 0, fontFamily: '"Cormorant Garamond", serif', fontWeight: 300, fontSize: 42, lineHeight: 1, color: T.text }}>
              The Atelier
            </h1>
            <div style={{ marginTop: 8, fontSize: 13, color: T.textSoft, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
              {loading ? "gathering submissions…" : `${submissions.length} submission${submissions.length === 1 ? "" : "s"} waiting`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={load}
              title="Refresh"
              style={{
                width: 38, height: 38, borderRadius: 12, cursor: "pointer",
                background: T.bgCard, border: `1px solid ${T.borderLight}`, color: T.textSoft, fontSize: 15,
              }}
            >
              ↻
            </button>
            <button
              onClick={() => setIsDark((v) => !v)}
              title="Toggle theme"
              style={{
                width: 38, height: 38, borderRadius: 12, cursor: "pointer",
                background: T.bgCard, border: `1px solid ${T.borderLight}`, color: T.accent, fontSize: 15,
              }}
            >
              {isDark ? "☀" : "☽"}
            </button>
          </div>
        </div>

        {/* States */}
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "80px 0", color: T.textMuted }}>
            <div style={{ width: 26, height: 26, border: `2px solid ${T.borderLight}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: 16 }}>Reading the icebergs…</span>
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: "40px 24px", borderRadius: 16, background: T.bgCard, border: `1px solid ${T.t3.chipBorder}`, textAlign: "center" }}>
            <div style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: 20, color: T.text, marginBottom: 6 }}>
              Couldn't reach the studio
            </div>
            <div style={{ fontSize: 13, color: T.textMuted }}>{error}</div>
            <button onClick={load} style={{ marginTop: 16, padding: "8px 20px", borderRadius: 12, border: `1px solid ${T.border}`, background: "none", color: T.accent, cursor: "pointer", fontFamily: '"Outfit", sans-serif' }}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && submissions.length === 0 && (
          <div style={{ padding: "90px 0", textAlign: "center" }}>
            <div style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: 26, color: T.textMuted }}>
              No submissions yet
            </div>
            <div style={{ fontSize: 13, color: T.textFaint, marginTop: 8 }}>
              When someone finishes the iceberg at /create, their card appears here.
            </div>
          </div>
        )}

        {/* Cards */}
        {!loading && !error && submissions.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {submissions.map((sub, i) => (
              <SubmissionCard
                key={sub.id}
                sub={sub}
                index={i}
                expanded={expandedId === sub.id}
                onToggle={() => setExpandedId((cur) => (cur === sub.id ? null : sub.id))}
                T={T}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
